import React, { useState } from 'react';

import {
  type Announcements,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { keyBy, last, uniqBy } from 'lodash';

import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import type { ProjectAssignee, ProjectTask, ProjectTaskStatus } from '~/services/ContractingService';

import TaskCard from './TaskCard';
import TaskColumn, { type DropIndicator } from './TaskColumn';
import { PROJECT_TASK_STATUSES, TASK_STATUS_LABELS } from './projectManagementModel';

interface DropTarget {
  status: ProjectTaskStatus;
  beforeTaskId?: string;
  indicator?: DropIndicator;
}

interface Props {
  tasks: Array<ProjectTask>;
  onChangeAssignees: (taskId: string, assignees: Array<ProjectAssignee>) => void;
  onMoveTask: (taskId: string, status: ProjectTaskStatus, beforeTaskId?: string) => void;
}

const isStatus = (id: unknown): id is ProjectTaskStatus => PROJECT_TASK_STATUSES.includes(id as ProjectTaskStatus);

// The pointer is inside both a card and its column, so prefer the card.
const collisionDetection: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  const cardHits = hits.filter((hit) => !isStatus(hit.id));
  if (cardHits.length) {
    return cardHits;
  }
  return hits.length ? hits : rectIntersection(args);
};

export default function TaskBoard({ tasks, onChangeAssignees, onMoveTask }: Props) {
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const query = search.trim().toLowerCase();
  const projectAssignees = uniqBy(
    tasks.flatMap((task) => task.assignees),
    'id',
  );
  const tasksById = keyBy(tasks, 'id');
  const visibleTasks = query ? tasks.filter((task) => task.name.toLowerCase().includes(query)) : tasks;
  // Drops are placed against every task, not just the ones the search shows, to match the saved order.
  const columnTaskIds = (status: ProjectTaskStatus, columnTasks = tasks) =>
    columnTasks.filter((task) => task.status === status).map((task) => task.id);

  // Returns null when the card would land where it already is.
  const findDropTarget = ({ active, over }: Pick<DragMoveEvent, 'active' | 'over'>): DropTarget | null => {
    if (!over || over.id === active.id) {
      return null;
    }
    const taskId = String(active.id);
    let target: DropTarget;
    if (isStatus(over.id)) {
      const lastVisibleId = last(columnTaskIds(over.id, visibleTasks));
      target = { status: over.id, indicator: lastVisibleId ? { taskId: lastVisibleId, side: 'bottom' } : undefined };
    } else {
      const overTask = tasksById[over.id];
      const otherIds = columnTaskIds(overTask.status).filter((id) => id !== taskId);
      const dragged = active.rect.current.translated;
      const isBelowMiddle = !!dragged && dragged.top + dragged.height / 2 > over.rect.top + over.rect.height / 2;
      target = {
        status: overTask.status,
        beforeTaskId: isBelowMiddle ? otherIds[otherIds.indexOf(overTask.id) + 1] : overTask.id,
        indicator: { taskId: overTask.id, side: isBelowMiddle ? 'bottom' : 'top' },
      };
    }

    const { status } = tasksById[taskId];
    const ids = columnTaskIds(status);
    const isSamePlace = target.status === status && target.beforeTaskId === ids[ids.indexOf(taskId) + 1];
    return isSamePlace ? null : target;
  };

  const describeTarget = (target: DropTarget | null) => {
    if (!target) {
      return 'It stays where it was.';
    }
    const before = target.beforeTaskId && tasksById[target.beforeTaskId].name;
    return `${TASK_STATUS_LABELS[target.status]}, ${before ? `before ${before}` : 'at the end'}.`;
  };
  const nameOf = (id: string | number) => tasksById[id]?.name ?? 'Task';
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}.`,
    onDragOver: (event) => `${nameOf(event.active.id)}: ${describeTarget(findDropTarget(event))}`,
    onDragEnd: (event) => `Dropped ${nameOf(event.active.id)}. ${describeTarget(findDropTarget(event))}`,
    onDragCancel: ({ active }) => `Moving ${nameOf(active.id)} was cancelled.`,
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const target = findDropTarget(event);
    setActiveId(null);
    setDropTarget(null);
    if (target) {
      onMoveTask(String(event.active.id), target.status, target.beforeTaskId);
    }
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search for task"
          size="small"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Typography variant="body2" color="text.secondary">
          Drag a task card to change its status or order.
        </Typography>
      </Stack>

      <DndContext
        accessibility={{ announcements }}
        collisionDetection={collisionDetection}
        sensors={sensors}
        onDragStart={({ active }) => setActiveId(String(active.id))}
        onDragMove={(event) => setDropTarget(findDropTarget(event))}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setActiveId(null);
          setDropTarget(null);
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: `repeat(${PROJECT_TASK_STATUSES.length}, minmax(220px, 1fr))`,
            overflowX: 'auto',
            pb: 1,
          }}
        >
          {PROJECT_TASK_STATUSES.map((status) => (
            <TaskColumn
              key={status}
              dropTarget={dropTarget?.status === status ? dropTarget : null}
              status={status}
              suggestedAssignees={projectAssignees}
              tasks={columnTaskIds(status, visibleTasks).map((id) => tasksById[id])}
              onChangeAssignees={onChangeAssignees}
            />
          ))}
        </Box>

        <DragOverlay dropAnimation={null}>
          {activeId && (
            <Box sx={{ borderRadius: 1, boxShadow: 8, cursor: 'grabbing' }}>
              <TaskCard
                suggestedAssignees={projectAssignees}
                task={tasksById[activeId]}
                onChangeAssignees={onChangeAssignees}
              />
            </Box>
          )}
        </DragOverlay>
      </DndContext>
    </>
  );
}
